/**
 * `app.fusegrid.io/embed.js` — the marketing-site embed loader.
 *
 * The marketing site is hand-written static HTML with no build step and no npm,
 * and it must stay that way. So the embed ships from the *app* origin as one
 * standalone script, and a page opts in with a single tag:
 *
 *   <div class="shot-frame" data-fg-tour="cfo-first-look"
 *        data-fg-poster="/shots/beacon-ceo.png"
 *        data-fg-cta="Take the 2-minute tour"></div>
 *   <script async src="https://app.fusegrid.io/embed.js"></script>
 *
 * **Lazy by click, deliberately.** `demo_session()` is rate-limited per IP, so
 * an auto-playing iframe on the homepage would mint a demo session per page
 * view and turn the 429 branch into the hero. The poster costs nothing and the
 * session is only minted when someone actually wants the tour.
 *
 * No dependencies, no framework, no build-time coupling to the app bundle.
 */

type EmbedEvent = {
  event: string
  demo?: { id: string; name: string; url: string }
  step?: { id: string; index: number }
}

const APP_ORIGIN = (() => {
  try {
    return new URL((document.currentScript as HTMLScriptElement | null)?.src || '').origin
  } catch {
    return 'https://app.fusegrid.io'
  }
})()

/** Below this the CFO shell's nav rail collapses and the tour reads poorly. */
const MIN_EMBED_WIDTH = 900

const STYLE = `
.fg-embed{position:relative;overflow:hidden}
.fg-embed img.fg-embed__poster{display:block;width:100%;height:auto}
.fg-embed__play{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
  background:#1b4965;color:#fffdf8;border:none;border-radius:999px;padding:12px 22px;
  font:600 15px/1.2 Inter,system-ui,sans-serif;cursor:pointer;box-shadow:0 6px 18px rgba(0,0,0,.18)}
.fg-embed__play:hover{background:#15384f}
.fg-embed.is-live .fg-embed__play,.fg-embed.is-live .fg-embed__poster{display:none}
.fg-embed iframe{position:absolute;inset:0;width:100%;height:100%;border:0;display:block}
.fg-embed.is-live{min-height:620px}
.fg-embed__popup{position:fixed;inset:0;z-index:2147483000;background:rgba(15,23,42,.7);padding:3vh 3vw}
.fg-embed__popup iframe{width:100%;height:100%;border:0;border-radius:10px;background:#fff;box-shadow:0 24px 80px rgba(0,0,0,.4)}
.fg-embed__close{position:absolute;top:calc(3vh - 14px);right:calc(3vw - 14px);width:32px;height:32px;border-radius:999px;
  border:none;background:#fff;color:#1b2a33;font-size:15px;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.3)}
`

function injectStyle(): void {
  if (document.getElementById('fg-embed-style')) return
  const style = document.createElement('style')
  style.id = 'fg-embed-style'
  style.textContent = STYLE
  document.head.appendChild(style)
}

/** Forward first-touch UTMs so an embedded tour attributes like any other CTA. */
function withAttribution(url: string): string {
  try {
    const target = new URL(url)
    const here = new URLSearchParams(window.location.search)
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = here.get(key)
      if (value) target.searchParams.set(key, value)
    }
    if (!target.searchParams.has('utm_source')) target.searchParams.set('utm_source', 'website_embed')
    return target.toString()
  } catch {
    return url
  }
}

/** Relay tour events to whatever analytics the host page already runs. */
function relay(payload: EmbedEvent): void {
  const plausible = (window as unknown as { plausible?: (goal: string, opts?: unknown) => void }).plausible
  if (payload.event === 'demo_finished') plausible?.('Tour Complete')
  else if (payload.event === 'lead_identify') plausible?.('Demo Lead')
  else if (payload.event === 'convert_cta') plausible?.('Signup Click')
  window.dispatchEvent(new CustomEvent('fusegrid-tour-event', { detail: payload }))
}

function activatePopup(slug: string): void {
  const overlay = document.createElement('div')
  overlay.className = 'fg-embed__popup'
  const frame = document.createElement('iframe')
  frame.src = withAttribution(
    `${APP_ORIGIN}/demo?fg_tour=${encodeURIComponent(slug)}` +
    `&fg_tour_embed=1&fg_tour_origin=${encodeURIComponent(window.location.origin)}`,
  )
  frame.title = 'FuseGrid interactive product tour'
  frame.setAttribute('allow', 'clipboard-write')
  frame.setAttribute('referrerpolicy', 'strict-origin')
  const close = document.createElement('button')
  close.className = 'fg-embed__close'
  close.type = 'button'
  close.textContent = '✕'
  close.setAttribute('aria-label', 'Close the tour')
  close.addEventListener('click', () => overlay.remove())
  overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') overlay.remove() })
  overlay.append(frame, close)
  document.body.appendChild(overlay)
  close.focus()
}

function activate(host: HTMLElement, slug: string): void {
  if (host.getAttribute('data-fg-mode') === 'popup') {
    activatePopup(slug)
    return
  }
  if (host.classList.contains('is-live')) return

  // Narrow viewports: hand off to a full tab rather than framing a shell that
  // has already collapsed its navigation.
  if (window.innerWidth < MIN_EMBED_WIDTH) {
    window.location.href = withAttribution(`${APP_ORIGIN}/demo?fg_tour=${encodeURIComponent(slug)}`)
    return
  }

  const src = withAttribution(
    `${APP_ORIGIN}/demo?fg_tour=${encodeURIComponent(slug)}` +
    `&fg_tour_embed=1&fg_tour_origin=${encodeURIComponent(window.location.origin)}`,
  )
  const frame = document.createElement('iframe')
  frame.src = src
  frame.title = 'FuseGrid interactive product tour'
  frame.setAttribute('allow', 'clipboard-write')
  frame.setAttribute('referrerpolicy', 'strict-origin')
  frame.setAttribute('loading', 'eager')
  host.classList.add('is-live')
  host.appendChild(frame)
}

function mount(host: HTMLElement): void {
  const slug = host.getAttribute('data-fg-tour')
  if (!slug || host.dataset.fgEmbedReady === '1') return
  host.dataset.fgEmbedReady = '1'
  host.classList.add('fg-embed')

  const poster = host.getAttribute('data-fg-poster')
  if (poster && !host.querySelector('img')) {
    const img = document.createElement('img')
    img.className = 'fg-embed__poster'
    img.src = poster
    img.alt = host.getAttribute('data-fg-alt') || 'FuseGrid product screenshot'
    img.loading = 'lazy'
    host.appendChild(img)
  }

  const play = document.createElement('button')
  play.type = 'button'
  play.className = 'fg-embed__play'
  play.textContent = host.getAttribute('data-fg-cta') || '▶ Take the tour'
  play.addEventListener('click', () => activate(host, slug))
  host.appendChild(play)

  // Without JS, or before this runs, the page still needs a way through.
  if (!host.querySelector('noscript')) {
    const fallback = document.createElement('noscript')
    const link = document.createElement('a')
    link.href = `${APP_ORIGIN}/demo?fg_tour=${encodeURIComponent(slug)}`
    link.textContent = 'Open the live demo'
    fallback.appendChild(link)
    host.appendChild(fallback)
  }
}

function boot(): void {
  injectStyle()
  document.querySelectorAll<HTMLElement>('[data-fg-tour]').forEach(mount)
}

window.addEventListener('message', (event: MessageEvent) => {
  // Only the app origin may speak for the tour.
  if (event.origin !== APP_ORIGIN) return
  const data = event.data as { message?: string; payload?: EmbedEvent } | null
  if (data?.message !== 'fusegrid-tour-event' || !data.payload) return
  relay(data.payload)
})

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}

export {}
